import { useState } from "react";
import { usePlanWorkbookWorkspace } from "../usePlanWorkbookWorkspace";
import type { ProductionPlanPreview, SavedProductionPlan, PreviewTab } from "./plan-import-types";
import { canRequestMaterial, procurementView, groupProcurementReviews } from "../production-material-procurement";
import { PAGE_SIZE } from "./plan-import-presentation";
import { usePlanRequestWorkspace, usePlanRequestDrawerEffect } from "./usePlanRequestWorkspace";
import { usePlanLibraryWorkspace } from "./usePlanLibraryWorkspace";
export function useProductionPlanImportWorkspace({ notify, onDraftImported }: {
    notify?: (message: string) => void;
    onDraftImported?: (weekStart: string) => void;
} = {}) {
    const [preview, setPreview] = useState<ProductionPlanPreview | null>(null);
    const [savedPlans, setSavedPlans] = useState<SavedProductionPlan[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState<PreviewTab>("SHORTAGES");
    const [page, setPage] = useState(1);
    const [weekMenuOpen, setWeekMenuOpen] = useState(false);
    const core = { preview, setPreview, savedPlans, setSavedPlans, selectedPlanId, setSelectedPlanId, error, setError, activeTab, setActiveTab, page, setPage, weekMenuOpen, setWeekMenuOpen };
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    const validRefreshTime = Boolean(preview && Number.isFinite(Date.parse(preview.refreshedAt)));
    const currentAssessment = preview?.procurementAssessment?.version === 3 &&
        validRefreshTime &&
        new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(preview.refreshedAt)) === today &&
        savedPlans.find((plan) => plan.id === selectedPlanId)?.status === "ACTIVE";
    const requests = usePlanRequestWorkspace({ ...core, today, currentAssessment, notify });
    const library = usePlanLibraryWorkspace({ ...core, ...requests, notify, onDraftImported });
    usePlanRequestDrawerEffect(requests);
    const { file, uploadOpen, setFile, setUploadOpen, setInspection, inspectFile, unsavedWorkbookRef } = library;
    const { activeRequestsReady, activeRequests, selectedMaterialCodes } = requests;
    const selectTab = (tab: PreviewTab) => {
        setActiveTab(tab);
        setPage(1);
    };
    const filteredMaterials = preview?.materials.filter((material) => activeTab === "MATERIALS" || procurementView(material) === (activeTab === "SHORTAGES" ? "PURCHASE" : activeTab)) ?? [];
    const activeItemCount = activeTab === "PLAN"
        ? preview?.rows.length ?? 0
        : filteredMaterials.length;
    const shouldPaginate = activeItemCount > PAGE_SIZE;
    const totalPages = shouldPaginate ? Math.ceil(activeItemCount / PAGE_SIZE) : 1;
    const pageStart = shouldPaginate ? (page - 1) * PAGE_SIZE : 0;
    const pageEnd = shouldPaginate
        ? Math.min(pageStart + PAGE_SIZE, activeItemCount)
        : activeItemCount;
    const visibleMaterials = filteredMaterials.slice(pageStart, pageEnd);
    const visiblePlanRows = preview?.rows.slice(pageStart, pageEnd) ?? [];
    const visibleSelectableMaterials = visibleMaterials.filter((material) => activeRequestsReady && canRequestMaterial(material, currentAssessment) && !activeRequests[material.materialCode]);
    const allVisibleSelected = visibleSelectableMaterials.length > 0 &&
        visibleSelectableMaterials.every((material) => selectedMaterialCodes.has(material.materialCode));
    const selectedSavedPlan = savedPlans.find((plan) => plan.id === selectedPlanId) ?? savedPlans[0] ?? null;
    const nativePlanSelected = !uploadOpen && selectedSavedPlan?.origin === "SYSTEM";
    const groupedReviews = groupProcurementReviews(preview?.materials ?? [], preview?.procurementAssessment?.issues ?? []);
    const workbookWorkspace = usePlanWorkbookWorkspace(file, uploadOpen || nativePlanSelected ? null : selectedPlanId || null, (updatedFile) => {
        setFile(updatedFile);
        setUploadOpen(true);
        setInspection(null);
        setPreview(null);
        void inspectFile(undefined, updatedFile);
    });
    unsavedWorkbookRef.current = workbookWorkspace.hasDrafts;
    return { ...core, ...requests, ...library, today, currentAssessment, validRefreshTime, filteredMaterials, activeItemCount, shouldPaginate, totalPages, pageStart, pageEnd, visibleMaterials, visiblePlanRows, visibleSelectableMaterials, allVisibleSelected, selectedSavedPlan, nativePlanSelected, groupedReviews, selectTab, workbookWorkspace };
}
export type ProductionPlanImportWorkspace = ReturnType<typeof useProductionPlanImportWorkspace>;
